import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SupabaseService } from '../supabase.service';

/**
 * Mesure d'audience exemptée de consentement (MOKA-355) — indépendante de GTM.
 *
 * Sur /sign-in et /sign-up, compte UNE visite par page et par onglet dans des compteurs
 * journaliers agrégés (page, utm_source, utm_medium, utm_campaign, domaine d'origine), via la
 * fonction `track_marketing_visit`. Rien n'est stocké sur l'appareil (ni cookie, ni
 * localStorage) : la mémoire de l'onglet suffit. Aucun identifiant, aucune adresse IP conservée.
 *
 * La campagne d'arrivée (première page suivie de l'onglet) est gardée en mémoire et transmise au
 * formulaire d'inscription (`signupSource`), qui la joint aux métadonnées du compte : c'est ce qui
 * relie une inscription, puis une organisation cliente, à sa campagne.
 */
export const AUDIENCE_PATHS = ['/sign-in', '/sign-up'] as const;
export type AudiencePath = (typeof AUDIENCE_PATHS)[number];

export interface CampaignSource {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  /** Domaine d'origine seulement (jamais l'adresse complète), ou null. */
  referrer: string | null;
  landing_path: AudiencePath;
}

/** Page suivie (chemin sans paramètre) et sa campagne, ou null si la page n'est pas suivie. */
export function campaignFromUrl(url: string, referrer: string, ownHost: string): CampaignSource | null {
  const [beforeHash] = url.split('#');
  const [path, query = ''] = beforeHash.split('?');
  if (!(AUDIENCE_PATHS as readonly string[]).includes(path)) return null;
  const params = new URLSearchParams(query);
  const pick = (key: string) => params.get(key)?.trim() || null;
  return {
    utm_source: pick('utm_source'),
    utm_medium: pick('utm_medium'),
    utm_campaign: pick('utm_campaign'),
    referrer: referrerDomain(referrer, ownHost),
    landing_path: path as AudiencePath,
  };
}

export function referrerDomain(referrer: string, ownHost: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, '');
    return host && host !== ownHost.replace(/^www\./, '') ? host : null;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AudienceService {
  private readonly supabase = inject(SupabaseService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  /** Pages déjà comptées dans cet onglet (mémoire seulement). */
  private readonly counted = new Set<AudiencePath>();
  /** Campagne d'arrivée de l'onglet, transmise à l'inscription. */
  readonly signupSource = signal<CampaignSource | null>(null);

  constructor() {
    const sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.onNavigation(e.urlAfterRedirects));
    inject(DestroyRef).onDestroy(() => sub.unsubscribe());
    if (this.router.navigated) this.onNavigation(this.router.url);
  }

  private onNavigation(url: string): void {
    const win = this.document.defaultView;
    const source = campaignFromUrl(url, this.document.referrer ?? '', win?.location.hostname ?? '');
    if (!source) return;
    if (!this.signupSource()) this.signupSource.set(source);
    if (this.counted.has(source.landing_path)) return;
    this.counted.add(source.landing_path);
    // Les onglets suivants de l'onglet gardent la campagne d'arrivée (même session de visite).
    const first = this.signupSource()!;
    void this.supabase.client
      .rpc('track_marketing_visit', {
        p_path: source.landing_path,
        p_utm_source: source.utm_source ?? first.utm_source,
        p_utm_medium: source.utm_medium ?? first.utm_medium,
        p_utm_campaign: source.utm_campaign ?? first.utm_campaign,
        p_referrer: first.referrer,
      })
      .then(({ error }) => {
        // La mesure ne doit jamais gêner la connexion : échec silencieux, compté à la prochaine visite.
        if (error) this.counted.delete(source.landing_path);
      });
  }
}
